export type ThemeName = 'messenger' | 'telegram' | 'github' | 'apple-liquid' | 'debug';

export interface ThemeOptions {
  defaultTheme?: ThemeName;
}

const THEME_CLASSES: ThemeName[] = ['messenger', 'telegram', 'github', 'apple-liquid', 'debug'];

/** Apply a theme to the document */
function applyTheme(theme: ThemeName) {
  const themeClass = `theme-${theme}`;

  THEME_CLASSES.forEach((t) => {
    document.documentElement.classList.remove(`theme-${t}`);
    document.body.classList.remove(`theme-${t}`);
  });

  document.documentElement.classList.add(themeClass);
  document.body.classList.add(themeClass);
  document.documentElement.setAttribute('data-theme', theme);
}

/** Fetch theme from /api/config (global configuration) */
async function fetchConfigTheme(): Promise<ThemeName | null> {
  try {
    const res = await fetch('/api/config');
    if (!res.ok) return null;
    const json = await res.json();
    const theme = json?.data?.['ui.theme'];
    if (theme && THEME_CLASSES.includes(theme as ThemeName)) {
      return theme as ThemeName;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Apply theme with priority: URL ?theme= param > global config > default.
 *
 * Synchronously applies URL param or default theme to prevent FOUC,
 * then asynchronously fetches from /api/config and re-applies if the
 * configured theme differs (only when no URL param override).
 */
export function applyThemeFromQuery(options: ThemeOptions = {}) {
  const { defaultTheme = 'apple-liquid' } = options;
  const themeParam = new URLSearchParams(window.location.search).get('theme') as ThemeName | null;
  const syncTheme = themeParam && THEME_CLASSES.includes(themeParam)
    ? themeParam
    : defaultTheme;

  // Immediate apply (synchronous)
  applyTheme(syncTheme);

  // Async: fetch config theme when no URL param override
  if (!themeParam) {
    fetchConfigTheme().then((configTheme) => {
      if (configTheme && configTheme !== syncTheme) {
        applyTheme(configTheme);
      }
    }).catch(() => {});
  }

  return syncTheme;
}
